<template>
  <div class="mx-xs-2" :class="section.StyleClass">
    <BaseAlert
      color="var(--orange-100)"
      class="border-sm border-solid border-warning"
      v-if="this.isExpired"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--orange-500)">mdi-alert</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span> This quote has expired.
          Please check all the details below are accurate and submit a new
          quote.
        </div>
      </div>
    </BaseAlert>
    <BaseAlert
      color="var(--red-100)"
      class="border-sm border-solid border-error"
      v-if="quoteFailed"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span>
          We couldn't get a quote right now, please try again in a few minutes.
        </div>
      </div>
    </BaseAlert>
    <BaseAlert
      color="var(--red-100)"
      class="border-sm border-solid border-error"
      v-if="quoteDeclined"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span> Unfortunately no insurer
          was able to offer you a quote at this time based on the details you
          have entered.
        </div>
      </div>
    </BaseAlert>
    <BaseAlert
      color="var(--red-100)"
      class="border-sm border-solid border-error"
      v-if="clientError"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span> There is a problem with
          the following questions:
          <ul>
            <li
              v-for="(message, index) in clientErrorMessagesArray"
              :key="index"
              class="font-weight-bold"
            >
              {{ message }}
            </li>
          </ul>
        </div>
      </div>
    </BaseAlert>
    <div class="elevation-3 rounded-lg pa-4 pa-xl-16 white">
      <BaseButton
        :text="`Back to '${section.PreviousStepTitle}'`"
        flat
        ripple
        class="font-weight-bold d-flex back-btn back-previous__btn-link"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="handleBack({ section })"
      />
      <!-- Stepper Title -->
      <v-row>
        <v-col class="d-flex justify-space-between flex-wrap section-title">
          <div>
            <h1 class="font-28 font-weight-bold black2-color">
              {{ section.SectionTitle }}
            </h1>
          </div>
          <BaseButton
            text="Save &amp; exit"
            flat
            depressed
            ripple
            :class="
              isSelfServe
                ? 'font-weight-bold d2c-save-exit mt-2 py-3 d-flex'
                : 'font-weight-bold mt-2 mr-2 px-1 py-3 d-flex'
            "
            icon="mdi-content-save"
            color="#106175"
            small
            @click="loadSaveAndExitDialog()"
          />
        </v-col>
      </v-row>

      <!-- QuickQuote message -->
      <v-alert
        color="var(--orange-100)"
        v-if="isQuickQuote && !isSelfServe"
        class="mt-5 col-xs-12 col-sm-12 col-md-8 col-lg-8 col-xl-8"
      >
        <div class="d-flex flex-no-wrap">
          <div class="mr-3">
            <v-icon color="#db7900">mdi-information</v-icon>
          </div>
          <div>
            <p>
              <span class="font-weight-bold"
                >Please review before continuing.</span
              >
              Some assumptions were made to provide you with a quick quote,
              which we have highlighted below.
            </p>
            <p class="mb-0">
              If <span class="font-weight-bold">incorrect</span>, please amend
              before continuing.
            </p>
          </div>
        </div>
      </v-alert>

      <!-- Quotation Questions -->
      <v-row>
        <v-col class="question-form">
          <ValidationObserver ref="observer">
            <v-form ref="form">
              <template v-for="question in section.Questions">
                <QuestionBase
                  :key="question.InterfaceKey"
                  :question="question"
                  :disabled="question.DisableOnError && showRentalError()"
                />
              </template>
            </v-form>
          </ValidationObserver>
        </v-col>
      </v-row>

      <!-- QuickQuote confirmation -->
      <v-row v-if="isQuickQuote && section.SectionNumber === getQuotesStep">
        <div
          class="mt-6 col-xs-12 col-sm-12 col-md-10 col-lg-10 col-xl-10 font-weight-bold"
        >
          By clicking ‘Get Quotes’ you confirm the assumptions we’ve highlighted
          are correct, or you’ve made the required amendments to reflect your
          client’s individual circumstances.
        </div>
      </v-row>

      <!-- Stepper Controls -->
      <v-row>
        <v-col class="d-flex align-center pt-1">
          <!-- submit btn -->
          <v-btn
            v-if="section.SectionNumber === getQuotesStep"
            :ripple="false"
            depressed
            rounded
            color="mustard"
            x-large
            class="font-weight-bold font-20 text-uppercase submit-btn continueBtn"
            @click.prevent="handleGetQuotes"
            :disabled="submitDisabled"
            >{{ section.SectionButtonText }}</v-btn
          >
          <!-- next btn -->
          <v-btn
            v-else
            :ripple="false"
            depressed
            rounded
            color="mustard"
            x-large
            :width="$vuetify.breakpoint.lgAndUp ? '487' : '100%'"
            class="font-weight-bold text-uppercase continueBtn"
            :class="
              !isSelfServe && $vuetify.breakpoint.mobile && currentStep === 1
                ? 'font-13'
                : 'font-20'
            "
            @click="clickNext()"
            :disabled="nextDisabled"
            >{{
              $vuetify.breakpoint.lgAndUp
                ? section.SectionButtonText
                : section.SectionButtonSmallText ?? section.SectionButtonText
            }}</v-btn
          >
        </v-col>
      </v-row>
    </div>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :handleNextData="{
            section: section,
            ref: $refs.form,
          }"
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :setDefaultUnapprovedMortgageApplication="
            section.SetDefaultUnapprovedMortgageApplication
          "
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading :title="title" />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>

    <BaseDialog
      ref="saveAndExitDialog"
      headerIcon="mdi-content-save"
      headerIconColor="#106175"
      windowClass="pa-0 pb-xl-0"
      headerClass="d2c-dialog-header d2c-save-exit-header pa-4 mb-n1"
      headerDivider
      :max-width="800"
      :isSelfServe="isSelfServe"
      dark2Icon
    >
      <template v-slot:content="{}">
        <SaveAndExit
          :sectionReminder="section.SectionReminder"
          :showUserRef="!section.SectionReminder"
          @saveAndExit="saveAndExit"
          @close="closeSaveAndExitDialog"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { scrollTop } from "@/common/utils";
import { EventBus } from "@/common/eventbus.js";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import QuestionBase from "@/components/Questions/QuestionBase";
import { CLIENTERROR, FAILED, UNABLE } from "@/constants/common.constants";
import { QUESTIONS_MODULE, STEPS_MODULE } from "@/store/types/module-types";
import Eligibility from "@/views/Dialog/Eligibility";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapState } from "vuex";
import { findError } from "@/common/question-set";
export default {
  name: "QuotationQuestions",
  components: {
    Eligibility,
    EligibilityBaseDialog,
    EligibilityLoading,
    EligibilityNotMet,
    QuestionBase,
    ValidationObserver,
    SaveAndExit: () => import("@/views/Dialog/SaveAndExit"),
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    BaseAlert: () => import("@/components/BaseComponents/BaseAlert.vue"),
  },
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getQuestionCoverType",
      "isSelfServe",
      "isExpired",
      "showRentalError",
      "propertyAnswersAsPropertyDetails",
      "clientErrorMessagesArray",
    ]),
    ...mapState(QUESTIONS_MODULE, [
      "lastQuoteErrorType",
      "form",
      "isQuickQuote",
      "questionMap",
    ]),
    ...mapState(STEPS_MODULE, ["getQuotesStep"]),
    ...mapGetters(STEPS_MODULE, ["totalSteps"]),
    nextDisabled() {
      return (
        this.currentStep === 2 &&
        (this.showRentalError() || this.isAboveRoomLimit)
      );
    },
    submitDisabled() {
      return this.showRentalError() || this.isAboveRoomLimit;
    },
    quoteFailed() {
      return this.lastQuoteErrorType === FAILED;
    },
    quoteDeclined() {
      return this.lastQuoteErrorType === UNABLE;
    },
    clientError() {
      return this.lastQuoteErrorType === CLIENTERROR;
    },
    isAboveRoomLimit() {
      const illegalNumberOfRooms = "NinePlus";
      return (
        this.questionMap.NumberOfBedrooms.Value === illegalNumberOfRooms ||
        this.questionMap.NumberOfBathrooms.Value === illegalNumberOfRooms
      );
    },
  },
  data() {
    return {
      isReadyToTrackSectionChanges: false,
    };
  },
  beforeMount() {
    scrollTop();
  },
  watch: {
    lastQuoteErrorType(newValue) {
      if (newValue !== null) {
        this.scrollToErrors();
      }
    },
  },
  methods: {
    ...mapActions(STEPS_MODULE, ["handleBack", "handleNext"]),
    ...mapActions(QUESTIONS_MODULE, [
      "handleSaveAndExit",
      "handleD2CSaveAndExit",
      "saveProgress",
      "resetInvalidQuestions",
    ]),
    scrollToErrors() {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 300);
    },

    async validateForm() {
      const form = this.$refs.form;
      const isValid = form.validate();

      if (!isValid) {
        const errors = findError(form);
        this.resetInvalidQuestions(errors);
      }
    },

    async clickNext() {
      sessionStorage.removeItem("isNewQuoteFromExpiredQuote");
      const isValid = await this.$refs.observer.validate();
      if (!this.isClaimFormValid() || !isValid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.saveProgress();
      this.handleNext({
        section: this.section,
        ref: this.$refs.form,
      });
    },
    async handleGetQuotes() {
      const isValid = await this.$refs.observer.validate();
      if (this.$refs.form.validate() && isValid) {
        this.$refs.dialog.open("Eligibility check");
      }
    },
    loadSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.open("Save & exit");
    },
    async saveAndExit(reference) {
      this.$refs.saveAndExitDialog.cancel();
      if (this.isSelfServe) {
        await this.validateForm();
        this.handleD2CSaveAndExit();
      } else {
        this.handleSaveAndExit(reference);
      }
    },
    closeSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.cancel();
    },
    isClaimFormValid() {
      if (!this.$refs.form.validate()) return false;

      const claimToggle = this.section.Questions.find(
        (question) =>
          question.AnswerControlType === "RadioButtonToggleHiddenQuestion"
      )?.Value;

      if (claimToggle === "true") {
        const claimFormIndex = this.section.Questions.findIndex(
          (question) => question.AnswerControlType === "LayoutTableBuilder"
        );

        if (
          claimFormIndex > -1 &&
          !this.section.Questions[claimFormIndex].Value.length
        ) {
          this.$set(
            this.section.Questions[claimFormIndex],
            "isClaimFormInvalid",
            true
          );
          return false;
        }
      }

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-btn.submit-btn {
  font-size: 20px;
  &:disabled {
    background-color: rgba(250, 181, 25, 0.5) !important;
    color: rgba(0, 0, 0, 0.6) !important;
    cursor: not-allowed !important;
    pointer-events: initial !important;
  }
  &:disabled:hover {
    background-color: rgba(250, 181, 25, 0.5) !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }
  &:disabled:hover::before {
    opacity: 0 !important;
  }
}
.font-weight-600 {
  font-weight: 600 !important;
}
.back-btn {
  margin-top: -33px !important;
  margin-left: -58px !important;
  margin-bottom: 25px !important;
}
.section-title {
  margin-left: -4px !important;
  margin-top: 0px !important;
}

// lg breakpoint
@media screen and (max-width: 992px) {
  .back-btn {
    margin-top: 7px !important;
    margin-left: -18px !important;
    margin-bottom: 10px !important;
  }
}
.continueBtn {
  height: 60px !important;
  color: #272727;
}

// sm breakpoint
@media screen and (max-width: 576px) {
  .continueBtn {
    height: 60px !important;
    width: 100%;
  }
  .submit-btn {
    width: 100%;
  }
}
</style>
